import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInventory } from "../../inventory/InventoryContext";
import { useAuth, isSuperAdmin } from "../../contexts/AuthContext";
import { useColumnPrefs } from "../../inventory/columnPrefs";
import { useSavedViews, viewQuery } from "../../inventory/savedViews";
import { FIELD_BY_KEY } from "../../inventory/fields";
import { applyFilters, readFilters, writeFilters, activeFilterCount } from "../../inventory/filters";
import { cellText, exportInventory, downloadTemplate } from "../../inventory/sheet";
import { uniqueCaseless } from "../../inventory/parse";
import Icon from "../../components/ui/Icon";
import Popover from "../../components/ui/Popover";
import Toast from "../../components/ui/Toast";
import FilterBar, { EMPTY_FILTERS } from "./FilterBar";
import InventoryTable from "./InventoryTable";
import ChannelDialog from "./ChannelDialog";
import ImportDialog from "./ImportDialog";
import ColumnPicker from "./ColumnPicker";
import AddToPlan from "./AddToPlan";
import ChannelDrawer from "./ChannelDrawer";
import SavedViews from "./SavedViews";
import "./InventoryPage.css";

const PAGE_SIZES = [25, 50, 100];

function compare(a, b, field) {
  if (field.numeric) {
    const x = a[field.key];
    const y = b[field.key];
    if (x == null && y == null) return 0;
    if (x == null) return 1; // blanks last either way round
    if (y == null) return -1;
    return x - y;
  }
  return cellText(a, field).localeCompare(cellText(b, field), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function InventoryPage() {
  const { channels, addChannel, updateChannel, removeChannels, importChannels, resetToSample, restore } =
    useInventory();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  // Only superadmins add, edit, delete or import channels; admins read them,
  // export them and put them in their plans (the API enforces it too)
  const canEdit = isSuperAdmin(user);
  const columnPrefs = useColumnPrefs(user?.email);
  const savedViews = useSavedViews(user?.email);
  const currentView = { query: viewQuery(params), layout: columnPrefs.layout };

  const applyView = (view) => {
    const next = new URLSearchParams(view.query);
    if (params.get("channel")) next.set("channel", params.get("channel"));
    setParams(next, { replace: true });
    columnPrefs.applyLayout(view.layout);
    setPage(0);
  };

  const filters = useMemo(() => readFilters(params), [params]);
  const sort = { key: params.get("sort") || "", dir: params.get("dir") === "desc" ? "desc" : "asc" };

  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [dialog, setDialog] = useState(null); // { type, channel? }
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);
  const navigate = useNavigate();

  // ?channel=<id> opens the detail drawer, so it can be linked and the
  // browser's Back button closes it
  const openChannel = channels.find((c) => c.id === params.get("channel")) || null;
  const showChannel = (row) => {
    const next = new URLSearchParams(params);
    next.set("channel", row.id);
    setParams(next);
  };
  const hideChannel = () => {
    const next = new URLSearchParams(params);
    next.delete("channel");
    setParams(next, { replace: true });
  };

  const planAdded = (message, planId) => {
    setSelected(new Set());
    setToast({ message, action: { label: "Open plan", run: () => navigate(`/plans/${planId}`) } });
  };

  // Drop selections for channels that no longer exist
  useEffect(() => {
    setSelected((s) => {
      const ids = new Set(channels.map((c) => c.id));
      const next = new Set([...s].filter((id) => ids.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [channels]);

  const setFilters = (next) => {
    setParams(writeFilters(next, params), { replace: true });
    setPage(0);
  };

  const onSort = (key) => {
    const next = new URLSearchParams(params);
    if (sort.key !== key) {
      next.set("sort", key);
      next.set("dir", "asc");
    } else if (sort.dir === "asc") {
      next.set("dir", "desc");
    } else {
      next.delete("sort");
      next.delete("dir");
    }
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => applyFilters(channels, filters), [channels, filters]);

  const sorted = useMemo(() => {
    const field = FIELD_BY_KEY[sort.key];
    if (!field) return filtered;
    const list = [...filtered].sort((a, b) => compare(a, b, field));
    if (sort.dir === "desc") {
      // keep blanks at the bottom when flipping numbers
      list.reverse();
      if (field.numeric) {
        const blanks = list.filter((r) => r[field.key] == null);
        return [...list.filter((r) => r[field.key] != null), ...blanks];
      }
    }
    return list;
  }, [filtered, sort.key, sort.dir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const columns = columnPrefs.visible;

  /* ---------- Summary ---------- */
  const stats = useMemo(() => {
    const partners = uniqueCaseless(filtered.map((c) => c.partner).filter(Boolean)).length;
    const territories = uniqueCaseless(filtered.flatMap((c) => c.territories || [])).length;
    // Average CPM for each currency on its own, most used first: EUR, USD
    // and TRY prices are never averaged together
    const byCurrency = {};
    filtered.forEach((c) => {
      if (c.cpm == null) return;
      (byCurrency[c.currency] = byCurrency[c.currency] || []).push(c.cpm);
    });
    const avgCpms = Object.entries(byCurrency)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([currency, cpms]) => ({ currency, avg: cpms.reduce((s, v) => s + v, 0) / cpms.length }));
    return { partners, territories, avgCpms };
  }, [filtered]);

  /* ---------- Selection ---------- */
  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (rows, on) =>
    setSelected((s) => {
      const next = new Set(s);
      rows.forEach((r) => (on ? next.add(r.id) : next.delete(r.id)));
      return next;
    });

  const selectedRows = channels.filter((c) => selected.has(c.id));

  /* ---------- Actions ---------- */
  const withUndo = (message, change) => {
    const snapshot = channels;
    change();
    setToast({ message, action: { label: "Undo", run: () => restore(snapshot, message) } });
  };

  const deleteRows = (rows) =>
    withUndo(
      rows.length === 1 ? `Deleted ${rows[0].channelName}` : `Deleted ${plural(rows.length, "channel")}`,
      () => removeChannels(rows.map((r) => r.id))
    );

  const saveChannel = (data) => {
    if (dialog.type === "edit") {
      updateChannel(dialog.channel.id, data);
      setToast({ message: `Saved ${data.channelName}` });
    } else {
      addChannel(data, dialog.type === "duplicate" ? "duplicate" : "add");
      setToast({ message: `Added ${data.channelName}` });
    }
    setDialog(null);
  };

  const doImport = (rows, mode, fileName) => {
    setDialog(null);
    const snapshot = channels;
    const { added, updated, unchanged } = importChannels(rows, mode, fileName);
    const parts = [];
    if (added) parts.push(`${added} added`);
    if (updated) parts.push(`${updated} updated`);
    if (unchanged) parts.push(`${unchanged} already up to date`);
    const message = `Import done: ${parts.join(", ") || "nothing changed"}`;
    setToast(
      added || updated
        ? { message, action: { label: "Undo", run: () => restore(snapshot, `import of ${fileName || "a file"}`) } }
        : { message }
    );
  };

  const doExport = (rows, bookType, close) => {
    exportInventory(rows, bookType);
    close();
  };

  const filterCount = activeFilterCount(filters);

  return (
    <div className="inv">
      <div className="inv-head">
        <div>
          <h1>Ad inventory</h1>
          <p>
            {plural(channels.length, "channel")} from{" "}
            {plural(uniqueCaseless(channels.map((c) => c.partner).filter(Boolean)).length, "partner")}
          </p>
        </div>

        <div className="inv-actions">
          {canEdit && (
            <button type="button" className="btn" onClick={() => setDialog({ type: "import" })}>
              <Icon name="upload" /> Import
            </button>
          )}

          <Popover
            align="right"
            trigger={
              <>
                <Icon name="download" /> Export <Icon name="chevron" size={14} />
              </>
            }
          >
            {(close) => (
              <>
                <div className="menu-label">
                  {filterCount ? `Filtered list (${sorted.length})` : `All channels (${sorted.length})`}
                </div>
                <button type="button" className="menu-item" disabled={!sorted.length} onClick={() => doExport(sorted, "xlsx", close)}>
                  <Icon name="file" /> Excel (.xlsx)
                </button>
                <button type="button" className="menu-item" disabled={!sorted.length} onClick={() => doExport(sorted, "csv", close)}>
                  <Icon name="file" /> CSV
                </button>
                {selectedRows.length > 0 && (
                  <>
                    <div className="menu-sep" />
                    <div className="menu-label">Selected ({selectedRows.length})</div>
                    <button type="button" className="menu-item" onClick={() => doExport(selectedRows, "xlsx", close)}>
                      <Icon name="file" /> Excel (.xlsx)
                    </button>
                    <button type="button" className="menu-item" onClick={() => doExport(selectedRows, "csv", close)}>
                      <Icon name="file" /> CSV
                    </button>
                  </>
                )}
              </>
            )}
          </Popover>

          {canEdit && (
            <Popover align="right" triggerClass="btn btn-square" label="More actions" trigger={<Icon name="more" />}>
              {(close) => (
                <>
                  <button
                    type="button"
                    className="menu-item"
                    onClick={() => {
                      downloadTemplate();
                      close();
                    }}
                  >
                    <Icon name="file" /> Download import template
                  </button>
                  <button
                    type="button"
                    className="menu-item"
                    onClick={() => {
                      close();
                      withUndo("Sample data restored", resetToSample);
                    }}
                  >
                    <Icon name="refresh" /> Restore sample data
                  </button>
                </>
              )}
            </Popover>
          )}

          {canEdit && (
            <button type="button" className="btn-primary" onClick={() => setDialog({ type: "add" })}>
              <Icon name="plus" /> Add channel
            </button>
          )}
        </div>
      </div>

      <div className="inv-stats">
        <div className="stat">
          <span>Channels</span>
          <strong>
            {filtered.length}
            {filterCount > 0 && <small> of {channels.length}</small>}
          </strong>
        </div>
        <div className="stat">
          <span>Partners</span>
          <strong>{stats.partners}</strong>
        </div>
        <div className="stat">
          <span>Territories</span>
          <strong>{stats.territories}</strong>
        </div>
        <div className="stat">
          <span>Average CPM</span>
          {stats.avgCpms.length === 0 ? (
            <strong>—</strong>
          ) : (
            <div className="stat-cpms">
              {stats.avgCpms.map((c) => (
                <strong key={c.currency}>
                  {c.avg.toFixed(2)} <small>{c.currency}</small>
                </strong>
              ))}
            </div>
          )}
        </div>
      </div>

      <FilterBar
        channels={channels}
        filters={filters}
        onChange={setFilters}
        leading={
          <SavedViews
            views={savedViews.views}
            current={currentView}
            onApply={applyView}
            onSave={(name) => {
              savedViews.saveView(name, currentView.query, currentView.layout);
              setToast({ message: `Saved view ${name}` });
            }}
            onUpdate={(id) => savedViews.updateView(id, currentView.query, currentView.layout)}
            onRename={savedViews.renameView}
            onDelete={savedViews.deleteView}
          />
        }
      />

      <div className="inv-card">
        <div className="inv-toolbar">
          {selected.size > 0 ? (
            <div className="bulk">
              <strong>{selected.size} selected</strong>
              <AddToPlan channels={selectedRows} onAdded={planAdded} triggerClass="btn btn-accent" />
              <button type="button" className="btn" onClick={() => exportInventory(selectedRows, "xlsx")}>
                <Icon name="download" /> Export
              </button>
              {canEdit && (
                <button type="button" className="btn" onClick={() => deleteRows(selectedRows)}>
                  <Icon name="trash" /> Delete
                </button>
              )}
              <button type="button" className="btn-link" onClick={() => setSelected(new Set())}>
                Clear selection
              </button>
            </div>
          ) : (
            <span className="inv-count">
              {sorted.length === channels.length
                ? plural(sorted.length, "channel")
                : `${plural(sorted.length, "channel")} match`}
            </span>
          )}

          <ColumnPicker
            columns={columnPrefs}
            trigger={
              <>
                <Icon name="columns" /> Columns
                {columnPrefs.hidden.length > 0 && (
                  <span className="filter-count">{columnPrefs.hidden.length} hidden</span>
                )}
              </>
            }
          />
        </div>

        {channels.length === 0 ? (
          <div className="inv-empty">
            <h2>{canEdit ? "Start your inventory" : "No channels yet"}</h2>
            <p>
              {canEdit
                ? "Import the AdMongrel ad inventory sheet, or add channels one at a time."
                : "Channels show up here once a superadmin adds them."}
            </p>
            {canEdit && (
              <div className="inv-empty-actions">
                <button type="button" className="btn-primary" onClick={() => setDialog({ type: "import" })}>
                  <Icon name="upload" /> Import a file
                </button>
                <button type="button" className="btn" onClick={() => setDialog({ type: "add" })}>
                  <Icon name="plus" /> Add channel
                </button>
              </div>
            )}
          </div>
        ) : sorted.length === 0 ? (
          <div className="inv-empty">
            <h2>No channels match</h2>
            <p>Try fewer filters or a different search.</p>
            <div className="inv-empty-actions">
              <button type="button" className="btn" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </button>
            </div>
          </div>
        ) : (
          <>
            <InventoryTable
              rows={pageRows}
              columns={columns}
              columnPrefs={columnPrefs}
              sort={sort}
              onSort={onSort}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onEdit={(channel) => setDialog({ type: "edit", channel })}
              onDuplicate={(channel) => setDialog({ type: "duplicate", channel })}
              onDelete={deleteRows}
              onOpen={showChannel}
              canEdit={canEdit}
            />

            <div className="inv-pager">
              <label>
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </label>
              <span>
                {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, sorted.length)} of{" "}
                {sorted.length}
              </span>
              <button
                type="button"
                className="icon-btn"
                aria-label="Previous page"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                <Icon name="chevron" className="rot-90" />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Next page"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                <Icon name="chevron" className="rot-270" />
              </button>
            </div>
          </>
        )}
      </div>

      {(dialog?.type === "add" || dialog?.type === "edit" || dialog?.type === "duplicate") && (
        <ChannelDialog
          mode={dialog.type}
          channel={dialog.channel}
          channels={channels}
          onSave={saveChannel}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === "import" && (
        <ImportDialog currentCount={channels.length} onImport={doImport} onClose={() => setDialog(null)} />
      )}

      {openChannel && (
        <ChannelDrawer
          channel={openChannel}
          onClose={hideChannel}
          canEdit={canEdit}
          onEdit={(channel) => setDialog({ type: "edit", channel })}
          onDuplicate={(channel) => setDialog({ type: "duplicate", channel })}
          onDelete={(channel) => {
            hideChannel();
            deleteRows([channel]);
          }}
          onAddedToPlan={planAdded}
        />
      )}

      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}
